<script setup lang="ts">
/**
 * UiKitChip — чип/тег HeroUI UI-kit. Figma: 483-52581.
 * Цвета только из токенов (классы hui-*).
 */
import { BG, BORDER, TEXT, TEXT_FG, type HuiColor, type HuiSize } from './palette'

type ChipVariant = 'solid' | 'bordered' | 'light' | 'flat' | 'faded' | 'shadow' | 'dot'
type ChipRadius = 'none' | 'sm' | 'md' | 'lg' | 'full'

const props = withDefaults(
  defineProps<{
    color?: HuiColor
    variant?: ChipVariant
    size?: HuiSize
    radius?: ChipRadius
    closable?: boolean
  }>(),
  { color: 'default', variant: 'solid', size: 'md', radius: 'full', closable: false },
)

defineEmits<{ (e: 'close'): void }>()

defineOptions({ name: 'UiKitChip' })

// Литералы для вариантов, которые нельзя собрать из palette (alpha / -700 / shadow-color)
const FLAT: Record<HuiColor, string> = {
  default: 'bg-hui-default/40 text-hui-default-foreground',
  primary: 'bg-hui-primary/20 text-hui-primary-700',
  secondary: 'bg-hui-secondary/20 text-hui-secondary-700',
  success: 'bg-hui-success/20 text-hui-success-700',
  warning: 'bg-hui-warning/20 text-hui-warning-700',
  danger: 'bg-hui-danger/20 text-hui-danger-700',
}
const SHADOW: Record<HuiColor, string> = {
  default: 'bg-hui-default text-hui-default-foreground shadow-lg shadow-hui-default/50',
  primary: 'bg-hui-primary text-hui-primary-foreground shadow-lg shadow-hui-primary/40',
  secondary: 'bg-hui-secondary text-hui-secondary-foreground shadow-lg shadow-hui-secondary/40',
  success: 'bg-hui-success text-hui-success-foreground shadow-lg shadow-hui-success/40',
  warning: 'bg-hui-warning text-hui-warning-foreground shadow-lg shadow-hui-warning/40',
  danger: 'bg-hui-danger text-hui-danger-foreground shadow-lg shadow-hui-danger/40',
}

const variantClass = computed(() => {
  const c = props.color
  switch (props.variant) {
    case 'solid': { return `${BG[c]} ${TEXT_FG[c]}`
    }
    case 'bordered': { return `bg-transparent border-2 ${BORDER[c]} ${TEXT[c]}`
    }
    case 'light': { return `bg-transparent ${TEXT[c]}`
    }
    case 'flat': { return FLAT[c]
    }
    case 'faded': { return `border-2 border-hui-default-200 bg-hui-default-100 ${TEXT[c]}`
    }
    case 'shadow': { return SHADOW[c]
    }
    case 'dot': { return 'bg-transparent border-2 border-hui-default-200 text-hui-foreground'
    }
    default: { return ''
    }
  }
})

const SIZE: Record<HuiSize, string> = {
  sm: 'h-6 px-2 text-xs gap-1',
  md: 'h-7 px-3 text-sm gap-1.5',
  lg: 'h-8 px-3 text-base gap-1.5',
}
const RADIUS: Record<ChipRadius, string> = {
  none: 'rounded-none',
  sm: 'rounded-hui-sm',
  md: 'rounded-hui-md',
  lg: 'rounded-hui-lg',
  full: 'rounded-full',
}
const DOT_COLOR = computed(() => BG[props.color])
</script>

<template>
  <div
    class="inline-flex items-center box-border whitespace-nowrap font-medium align-middle"
    :class="[variantClass, SIZE[size], RADIUS[radius]]"
  >
    <span v-if="variant === 'dot'" class="w-2 h-2 rounded-full shrink-0" :class="DOT_COLOR" />
    <slot name="leading" />
    <span class="truncate"><slot /></span>
    <slot name="trailing" />
    <button
      v-if="closable"
      type="button"
      class="shrink-0 inline-flex items-center justify-center -mr-1 w-4 h-4 rounded-full opacity-70 hover:opacity-100 transition-opacity outline-none focus-visible:ring-1 focus-visible:ring-current"
      aria-label="Удалить"
      @click="$emit('close')"
    >
      <svg class="w-3 h-3" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="2.5">
        <path d="M6 6l8 8M14 6l-8 8" stroke-linecap="round" />
      </svg>
    </button>
  </div>
</template>
